/**
 * Preview Landing Page — componentă extrasă la nivel de modul pentru a evita
 * re-crearea la fiecare render al editorului și pierderea stării (stage / formular).
 * Starea internă (stage, gdprChecked) rămâne stabilă la click și la tastare în editor.
 */
import { useState, useEffect, useRef } from "react";

const DEMO_DATA = {
  "Nume complet": "Maria Ionescu", "Prenume": "Maria", "Telefon": "0745 123 456",
  "Email": "maria@email.com", "Județ": "București", "Oraș": "București",
  "Adresă": "Str. Florilor 12", "Data nașterii": "15/03/1985",
  "Marca mașinii": "Dacia", "Model": "Logan", "An fabricație": "2021",
  "Nr. înmatriculare": "B 123 ABC", "Suprafață (mp)": "75",
  "Mesaj": "Doresc o ofertă...", "Cod poștal": "010101",
  "Nume": "Maria",
};

function AdScreen({ config, onFinish, isMobile, step }) {
  const [seconds, setSeconds] = useState(config?.adDuration ?? 15);
  const [canSkip, setCanSkip] = useState(false);

  useEffect(() => {
    if (step === 1) {
      setSeconds(config?.adDuration ?? 15);
      setCanSkip(false);
      return;
    }
    if (seconds <= 0) { onFinish(); return; }
    const t = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, step, config?.adDuration, onFinish]);

  useEffect(() => {
    const duration = config?.adDuration ?? 15;
    if (seconds <= duration - 5) setCanSkip(true);
  }, [seconds, config?.adDuration]);

  const w = isMobile ? 340 : 500;

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    let videoId = "";
    try {
      if (url.includes("youtube.com/watch?v=")) videoId = url.split("v=")[1].split("&")[0];
      else if (url.includes("youtu.be/")) videoId = url.split("youtu.be/")[1].split("?")[0];
      else if (url.includes("youtube.com/shorts/")) videoId = url.split("shorts/")[1].split("?")[0];
    } catch (e) { return null; }
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=1&loop=1&playlist=${videoId}` : null;
  };

  const activeLocalVideoUrl = config?.previewVideoUrl || config?.localVideoUrl;
  const embedUrl = getYouTubeEmbedUrl(config?.adVideoUrl);
  const duration = config?.adDuration ?? 15;
  const culoare = config?.culoare ?? "#2563EB";

  return (
    <div style={{ width: w, borderRadius: 14, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", background: config?.adBgColor || "#0F172A", position: "relative", minHeight: 500, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "Segoe UI, sans-serif" }}>
      {config?.adType === "video" ? (
        <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1, background: "black" }}>
          {activeLocalVideoUrl ? (
            <video src={activeLocalVideoUrl} controls autoPlay muted playsInline loop style={{ width: "100%", height: "100%", objectFit: "contain" }} title="Video Reclamă" />
          ) : embedUrl ? (
            <iframe src={embedUrl} title="Video Ad" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" style={{ width: "100%", height: "100%", border: "none" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 14 }}>Aștept link YouTube sau încarcă un MP4...</div>
          )}
        </div>
      ) : (
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px", textAlign: "center" }}>
          <div style={{ fontSize: isMobile ? 32 : 48, marginBottom: 16 }}>📣</div>
          <div style={{ fontSize: isMobile ? 18 : 24, fontWeight: 800, color: "white", lineHeight: 1.3, marginBottom: 12 }}>{config?.adText || "Textul reclamei tale apare aici"}</div>
          {config?.adSubtext && <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", marginBottom: 20 }}>{config.adSubtext}</div>}
          {config?.adUrl && <div style={{ display: "inline-block", padding: "10px 24px", borderRadius: 8, background: culoare, color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Află mai mult →</div>}
        </div>
      )}
      <div style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", borderRadius: 20, padding: "6px 14px", display: "flex", alignItems: "center", gap: 6, zIndex: 10 }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "white" }}>{seconds}</div>
        {canSkip && <span role="button" tabIndex={0} onClick={onFinish} onKeyDown={(e) => e.key === "Enter" && onFinish()} style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", cursor: "pointer", fontWeight: 600 }}>Sari →</span>}
      </div>
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "rgba(255,255,255,0.15)", zIndex: 10 }}>
        <div style={{ height: "100%", background: culoare, width: `${((duration - seconds) / duration) * 100}%`, transition: "width 1s linear" }} />
      </div>
    </div>
  );
}

function PreviewFormContent({ campuriActive, culoare, smsValidare }) {
  return (
    <div style={{ padding: "20px 24px" }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#1E293B", marginBottom: 4 }}>Completează datele tale</div>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 16 }}>Te contactăm cu cea mai bună ofertă</div>
      {["Nume complet *", "Email *", "Telefon *"].map((label, i) => (
        <div key={`std-${i}`} style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block", marginBottom: 3 }}>{label}</label>
          <input readOnly value={DEMO_DATA[label.replace(" *", "")] || "..."} style={{ width: "100%", padding: "9px 11px", borderRadius: 7, border: "1px solid #E2E8F0", fontSize: 13, background: "#F8FAFC", boxSizing: "border-box", color: "#374151" }} />
        </div>
      ))}
      {campuriActive?.map((c) => (
        <div key={c.label} style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block", marginBottom: 3 }}>{c.label}</label>
          {c.type === "datetime-local" ? (
            <input type="datetime-local" readOnly value={DEMO_DATA[c.label] || "2025-02-25T10:00"} style={{ width: "100%", padding: "9px 11px", borderRadius: 7, border: "1px solid #E2E8F0", fontSize: 13, background: "#F8FAFC", boxSizing: "border-box", color: "#374151" }} />
          ) : (
            <input readOnly value={DEMO_DATA[c.label] || "..."} style={{ width: "100%", padding: "9px 11px", borderRadius: 7, border: "1px solid #E2E8F0", fontSize: 13, background: "#F8FAFC", boxSizing: "border-box", color: "#374151" }} />
          )}
        </div>
      ))}
      {smsValidare && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "10px 0", padding: "8px 10px", background: "#FFF7ED", borderRadius: 7, fontSize: 11, color: "#92400E" }}>
          📱 Telefonul tău va fi verificat prin SMS
        </div>
      )}
      <button type="button" style={{ width: "100%", padding: "13px", borderRadius: 9, border: "none", background: culoare, color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer", marginTop: 6 }}>Trimite cererea</button>
      <div style={{ fontSize: 10, color: "#94A3B8", textAlign: "center", marginTop: 8 }}>Datele tale sunt protejate conform GDPR</div>
    </div>
  );
}

function PreviewGDPRScreen({ gdprChecked, setGdprChecked, culoare, onContinue }) {
  return (
    <div style={{ padding: "28px 24px", textAlign: "center" }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#1E293B", marginBottom: 8 }}>Înainte să continuăm</div>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 20, lineHeight: 1.6 }}>Pentru a-ți putea trimite oferta, avem nevoie de acordul tău pentru prelucrarea datelor personale.</div>
      <div role="button" tabIndex={0} onClick={() => setGdprChecked(!gdprChecked)} onKeyDown={(e) => e.key === "Enter" && setGdprChecked(!gdprChecked)} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", background: gdprChecked ? `${culoare}10` : "#F8FAFC", border: `1px solid ${gdprChecked ? culoare : "#E2E8F0"}`, borderRadius: 8, cursor: "pointer", textAlign: "left", marginBottom: 16, transition: "all 0.15s" }}>
        <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${gdprChecked ? culoare : "#CBD5E1"}`, background: gdprChecked ? culoare : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{gdprChecked && <span style={{ color: "white", fontSize: 11 }}>✓</span>}</div>
        <span style={{ fontSize: 11, color: "#374151", lineHeight: 1.5 }}>Sunt de acord cu <span style={{ color: culoare, textDecoration: "underline" }}>Politica de confidențialitate</span> și prelucrarea datelor mele personale în scopul primirii unei oferte de asigurare.</span>
      </div>
      <button type="button" onClick={() => gdprChecked && onContinue()} style={{ width: "100%", padding: "13px", borderRadius: 9, border: "none", background: gdprChecked ? culoare : "#E2E8F0", color: gdprChecked ? "white" : "#94A3B8", fontSize: 14, fontWeight: 700, cursor: gdprChecked ? "pointer" : "not-allowed", transition: "all 0.2s" }}>
        {gdprChecked ? "Continuă →" : "Bifează acordul pentru a continua"}
      </button>
    </div>
  );
}

function PreviewLPBody({ textExtra, buton, culoare, onCtaClick, compact, white }) {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onCtaClick === "function") {
      console.log("CTA Apasat");
      onCtaClick();
    }
  };
  return (
    <>
      {textExtra && <div style={{ fontSize: 12, color: white ? "rgba(255,255,255,0.85)" : "#374151", marginBottom: 16 }} dangerouslySetInnerHTML={{ __html: textExtra }} />}
      <button type="button" onClick={handleClick} style={{ width: "100%", padding: compact ? "12px" : "14px", borderRadius: 9, border: "none", background: white ? "white" : culoare, color: white ? culoare : "white", fontSize: compact ? 13 : 15, fontWeight: 700, cursor: "pointer", boxShadow: white ? "0 4px 12px rgba(0,0,0,0.15)" : "none" }}>{buton || "Solicită ofertă gratuită"}</button>
      <div style={{ fontSize: 10, color: white ? "rgba(255,255,255,0.5)" : "#94A3B8", textAlign: "center", marginTop: 8 }}>Fără obligații • Gratuit • GDPR compliant</div>
    </>
  );
}

function PreviewVideoBlock({ videoUrl, style }) {
  if (!videoUrl) return null;
  return (
    <video src={videoUrl} controls autoPlay muted playsInline loop className="w-full rounded-xl shadow-lg" style={{ width: "100%", borderRadius: 12, marginBottom: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", ...style }} title="Video pagină" />
  );
}

export default function LPPreview({ config, isMobile, step }) {
  const layout = config?.layout ?? "classic";
  const [stage, setStage] = useState(() => (layout === "ad" ? "ad" : "lp"));
  const [gdprChecked, setGdprChecked] = useState(false);
  const prevLayoutStepRef = useRef({ layout, step });

  useEffect(() => {
    const prev = prevLayoutStepRef.current;
    const layoutChanged = prev.layout !== layout;
    const stepChanged = prev.step !== step;
    if (layoutChanged || stepChanged) {
      prevLayoutStepRef.current = { layout, step };
      setStage(layout === "ad" ? "ad" : "lp");
      setGdprChecked(false);
    }
  }, [layout, step]);

  if (!config || typeof config !== "object") return null;

  const titlu = config.titlu ?? "";
  const subtitlu = config.subtitlu ?? "";
  const buton = config.buton ?? "Solicită ofertă gratuită";
  const culoare = config.culoare ?? "#2563EB";
  const campuriRaw = Array.isArray(config.campuri) ? config.campuri : [];
  const smsValidare = config.smsValidare !== false;
  const textExtra = config.textExtra ?? "";
  const activeVideoUrl = config.previewVideoUrl || config.localVideoUrl;

  const campuriActive = campuriRaw.filter(c => c && c.activ);
  const w = isMobile ? 340 : 500;

  const handleCtaClick = () => {
    setStage("gdpr");
  };

  const baseStyle = {
    width: w,
    background: "white",
    borderRadius: 14,
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    fontFamily: "Segoe UI, sans-serif",
    overflow: "hidden",
  };

  if (stage === "ad") {
    return <AdScreen config={config} isMobile={isMobile} onFinish={() => setStage("lp")} step={step} />;
  }

  if (stage === "gdpr" || stage === "form") {
    return (
      <div style={{ ...baseStyle, position: "relative", visibility: "visible", opacity: 1, display: "block" }}>
        <div style={{ filter: "blur(2px)", opacity: 0.3, background: culoare, padding: "24px 28px", color: "white", textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{titlu || "Titlul paginii"}</div>
          <div style={{ fontSize: 12 }}>{subtitlu || "Subtitlu"}</div>
        </div>
        <div style={{ background: "white", visibility: "visible", opacity: 1 }}>
          {stage === "gdpr" ? (
            <PreviewGDPRScreen gdprChecked={gdprChecked} setGdprChecked={setGdprChecked} culoare={culoare} onContinue={() => setStage("form")} />
          ) : (
            <PreviewFormContent campuriActive={campuriActive} culoare={culoare} smsValidare={smsValidare} />
          )}
        </div>
        <div role="button" tabIndex={0} onClick={() => setStage(stage === "gdpr" ? "lp" : "gdpr")} onKeyDown={(e) => e.key === "Enter" && setStage(stage === "gdpr" ? "lp" : "gdpr")} style={{ textAlign: "center", padding: "8px", fontSize: 11, color: "#94A3B8", cursor: "pointer", borderTop: "1px solid #F1F5F9" }}>← Înapoi</div>
      </div>
    );
  }

  if (layout === "split") return (
    <div style={{ ...baseStyle, display: "flex", flexDirection: "column", minHeight: 420 }}>
      {activeVideoUrl && <div style={{ padding: "12px 12px 0" }}><PreviewVideoBlock videoUrl={activeVideoUrl} /></div>}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, background: culoare, padding: "32px 22px", color: "white", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 800, lineHeight: 1.3, marginBottom: 10 }}>{titlu || "Titlul paginii"}</div>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 16 }}>{subtitlu || "Subtitlu"}</div>
        </div>
        <div style={{ flex: 1, padding: "32px 20px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <PreviewLPBody textExtra={textExtra} buton={buton} culoare={culoare} onCtaClick={handleCtaClick} compact />
        </div>
      </div>
    </div>
  );

  if (layout === "minimal") return (
    <div style={{ ...baseStyle, padding: isMobile ? "32px 24px" : "48px 40px" }}>
      {activeVideoUrl && <PreviewVideoBlock videoUrl={activeVideoUrl} />}
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: isMobile ? 18 : 26, fontWeight: 800, color: "#0F172A", marginBottom: 8 }}>{titlu || "Titlul paginii"}</div>
        <div style={{ fontSize: 13, color: "#64748B" }}>{subtitlu || "Subtitlu"}</div>
      </div>
      <PreviewLPBody textExtra={textExtra} buton={buton} culoare={culoare} onCtaClick={handleCtaClick} />
    </div>
  );

  if (layout === "card") return (
    <div style={{ ...baseStyle, background: `linear-gradient(135deg, ${culoare}EE, ${culoare}99)`, padding: isMobile ? "32px 20px" : "44px 30px", minHeight: 420, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      {activeVideoUrl && <div style={{ width: "100%", marginBottom: 16 }}><PreviewVideoBlock videoUrl={activeVideoUrl} style={{ marginBottom: 0 }} /></div>}
      <div style={{ width: "88%", background: "white", borderRadius: 14, padding: "24px 20px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize: isMobile ? 15 : 18, fontWeight: 800, color: "#0F172A", marginBottom: 4, textAlign: "center" }}>{titlu || "Titlul paginii"}</div>
        <div style={{ fontSize: 12, color: "#64748B", textAlign: "center", marginBottom: 20 }}>{subtitlu || "Subtitlu"}</div>
        <PreviewLPBody textExtra={textExtra} buton={buton} culoare={culoare} onCtaClick={handleCtaClick} compact />
      </div>
    </div>
  );

  if (layout === "ad") return (
    <div style={{ ...baseStyle }}>
      <div style={{ background: culoare, padding: "28px 28px 24px", color: "white", textAlign: "center" }}>
        <div style={{ fontSize: isMobile ? 17 : 21, fontWeight: 800, lineHeight: 1.3, marginBottom: 6 }}>{titlu || "Titlul paginii"}</div>
        <div style={{ fontSize: 12, opacity: 0.85 }}>{subtitlu || "Subtitlu"}</div>
      </div>
      <div style={{ padding: "20px 24px" }}>
        <div style={{ padding: "10px 14px", background: "#F0FDF4", borderRadius: 8, fontSize: 11, color: "#166534", marginBottom: 16, textAlign: "center" }}>Reclama s-a terminat — acum apare pagina de ofertă</div>
        <PreviewLPBody textExtra={textExtra} buton={buton} culoare={culoare} onCtaClick={handleCtaClick} />
      </div>
    </div>
  );

  return (
    <div style={baseStyle}>
      {activeVideoUrl && <div style={{ padding: "12px 12px 0" }}><PreviewVideoBlock videoUrl={activeVideoUrl} /></div>}
      <div style={{ background: culoare, padding: isMobile ? "24px 20px" : "36px 28px", color: "white", textAlign: "center" }}>
        <div style={{ fontSize: isMobile ? 18 : 23, fontWeight: 800, lineHeight: 1.3, marginBottom: 8 }}>{titlu || "Titlul paginii"}</div>
        <div style={{ fontSize: 13, opacity: 0.85 }}>{subtitlu || "Subtitlu"}</div>
      </div>
      <div style={{ padding: isMobile ? "20px" : "24px 28px" }}>
        <PreviewLPBody textExtra={textExtra} buton={buton} culoare={culoare} onCtaClick={handleCtaClick} />
      </div>
    </div>
  );
}
